import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '../../App';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Building2, Globe, Phone, Mail, RefreshCw } from 'lucide-react';

const categories = [
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'retail', label: 'Retail' },
  { value: 'service', label: 'Service' },
  { value: 'school', label: 'School' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'other', label: 'Other' }
];

const AdminBusinesses = () => {
  const { token } = useAuth();
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    name: '', category: 'service', description: '', address: '', phone: '', email: '', website: '',
    logo_url: '', hours: '', social_facebook: '', social_instagram: ''
  });

  const fetchBusinesses = async () => {
    try {
      const response = await axios.get(`${API}/businesses`);
      setBusinesses(response.data);
    } catch (error) {
      console.error('Failed to fetch:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBusinesses(); }, []);

  const handleSubmit = async () => {
    if (!formData.name || !formData.description || !formData.address) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      if (editing) {
        await axios.put(`${API}/businesses/${editing}`, formData, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Business updated!');
      } else {
        await axios.post(`${API}/businesses`, formData, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Business added!');
      }
      setShowDialog(false);
      setFormData({ name: '', category: 'service', description: '', address: '', phone: '', email: '', website: '', logo_url: '', hours: '', social_facebook: '', social_instagram: '' });
      setEditing(null);
      fetchBusinesses();
    } catch (error) {
      toast.error('Failed to save');
    }
  };

  const handleEdit = (item) => {
    setFormData({
      name: item.name, category: item.category, description: item.description, address: item.address,
      phone: item.phone || '', email: item.email || '', website: item.website || '',
      logo_url: item.logo_url || '', hours: item.hours || '',
      social_facebook: item.social_facebook || '', social_instagram: item.social_instagram || ''
    });
    setEditing(item.id);
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this business?')) return;
    try {
      await axios.delete(`${API}/businesses/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Deleted!');
      fetchBusinesses();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Building2 className="w-6 h-6 text-emerald-500" /> Business Directory
          </h1>
          <p className="text-slate-600">Manage local businesses and schools</p>
        </div>
        <Dialog open={showDialog} onOpenChange={(open) => {
          setShowDialog(open);
          if (!open) { setEditing(null); setFormData({ name: '', category: 'service', description: '', address: '', phone: '', email: '', website: '', logo_url: '', hours: '', social_facebook: '', social_instagram: '' }); }
        }}>
          <DialogTrigger asChild>
            <Button className="bg-plp-blue hover:bg-plp-blue-light"><Plus className="w-4 h-4 mr-2" /> Add Business</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing ? 'Edit Business' : 'Add Business'}</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Name *</Label><Input value={formData.name} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} /></div>
                <div><Label>Category</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData(p => ({ ...p, category: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{categories.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Description *</Label><Textarea value={formData.description} onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))} rows={3} /></div>
              <div><Label>Address *</Label><Input value={formData.address} onChange={(e) => setFormData(p => ({ ...p, address: e.target.value }))} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Phone</Label><Input value={formData.phone} onChange={(e) => setFormData(p => ({ ...p, phone: e.target.value }))} /></div>
                <div><Label>Email</Label><Input value={formData.email} onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Website</Label><Input value={formData.website} onChange={(e) => setFormData(p => ({ ...p, website: e.target.value }))} /></div>
                <div><Label>Hours</Label><Input value={formData.hours} onChange={(e) => setFormData(p => ({ ...p, hours: e.target.value }))} placeholder="Mon-Fri 9am-5pm" /></div>
              </div>
              <div><Label>Logo URL</Label><Input value={formData.logo_url} onChange={(e) => setFormData(p => ({ ...p, logo_url: e.target.value }))} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Facebook</Label><Input value={formData.social_facebook} onChange={(e) => setFormData(p => ({ ...p, social_facebook: e.target.value }))} placeholder="https://facebook.com/..." /></div>
                <div><Label>Instagram</Label><Input value={formData.social_instagram} onChange={(e) => setFormData(p => ({ ...p, social_instagram: e.target.value }))} placeholder="https://instagram.com/..." /></div>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setShowDialog(false)} className="flex-1">Cancel</Button>
                <Button onClick={handleSubmit} className="flex-1 bg-plp-blue">{editing ? 'Update' : 'Add'}</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-plp-blue" /></div>
      ) : businesses.length === 0 ? (
        <Card className="bg-white"><CardContent className="py-12 text-center"><Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" /><p className="text-slate-500">No businesses yet</p></CardContent></Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {businesses.map((item) => (
            <Card key={item.id} className="bg-white">
              <CardContent className="p-4">
                <div className="flex gap-4">
                  {item.logo_url ? (
                    <img src={item.logo_url} alt={item.name} className="w-16 h-16 object-cover rounded-lg" />
                  ) : (
                    <div className="w-16 h-16 bg-slate-100 rounded-lg flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-slate-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <Badge variant="outline" className="mb-1 capitalize">{item.category}</Badge>
                    <h3 className="font-heading font-bold truncate">{item.name}</h3>
                    <p className="text-sm text-slate-600 truncate">{item.address}</p>
                    <div className="flex gap-3 mt-2 text-xs text-slate-500">
                      {item.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{item.phone}</span>}
                      {item.website && <a href={item.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-plp-blue"><Globe className="w-3 h-3" />Website</a>}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(item)}><Pencil className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(item.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminBusinesses;
